import axios, { AxiosError, InternalAxiosRequestConfig } from 'axios';

// Access token lives in memory for fast synchronous reads by the request interceptor.
// sessionStorage is the durable backing store — see authService.ts TOKEN_KEY.
declare global {
  interface Window { __AUTH_TOKEN__: string | null; }
}

const SESSION_TOKEN_KEY = 'permus-it-token'; // must match TOKEN_KEY in authService.ts

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL:         BASE_URL,
  withCredentials: true, // sends httpOnly refresh-token cookie on every request
  headers:         { 'Content-Type': 'application/json' },
});

// ── Request interceptor: attach access token ──────────────────────────────────

api.interceptors.request.use((cfg: InternalAxiosRequestConfig) => {
  const token = window.__AUTH_TOKEN__;
  if (token) cfg.headers.set('Authorization', `Bearer ${token}`);
  return cfg;
});

// ── Response interceptor: silent refresh on 401 with request queuing ──────────

let isRefreshing = false;
type QueueEntry  = { resolve: (token: string) => void; reject: (err: unknown) => void };
let pendingQueue: QueueEntry[] = [];

function processQueue(token: string | null, error: unknown = null): void {
  pendingQueue.forEach(({ resolve, reject }) => {
    if (token) resolve(token);
    else reject(error);
  });
  pendingQueue = [];
}

api.interceptors.response.use(
  res => res,
  async (err: AxiosError) => {
    const orig = err.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Only intercept 401s; skip if already retried or if this IS an auth endpoint.
    // /auth/azure-sso 401s must propagate to AuthCallback so it can show an error —
    // only /auth/refresh and /auth/me 401s mean the session is truly dead.
    if (
      err.response?.status !== 401 ||
      orig._retry ||
      orig.url?.startsWith('/auth/')
    ) {
      if (
        err.response?.status === 401 &&
        (orig.url === '/auth/refresh' || orig.url === '/auth/me') &&
        window.location.pathname !== '/auth/callback' &&
        window.location.pathname !== '/login'   // already on /login → don't reload-loop
      ) {
        window.__AUTH_TOKEN__ = null;
        try { sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch {}
        window.location.href  = '/login';
      }
      return Promise.reject(err);
    }

    orig._retry = true;

    if (isRefreshing) {
      // Another request already triggered a refresh — queue this one until it resolves
      return new Promise<any>((resolve, reject) => {
        pendingQueue.push({
          resolve: (token: string) => {
            orig.headers.set('Authorization', `Bearer ${token}`);
            resolve(api(orig));
          },
          reject,
        });
      });
    }

    isRefreshing = true;

    try {
      // Call refresh directly with plain axios to avoid our own interceptor
      const { data } = await axios.post<{ token: string }>(
        `${BASE_URL}/auth/refresh`,
        {},
        { withCredentials: true },
      );

      window.__AUTH_TOKEN__ = data.token;
      try { sessionStorage.setItem(SESSION_TOKEN_KEY, data.token); } catch {}
      processQueue(data.token);
      orig.headers.set('Authorization', `Bearer ${data.token}`);
      return api(orig);
    } catch (refreshError) {
      processQueue(null, refreshError);
      window.__AUTH_TOKEN__ = null;
      try { sessionStorage.removeItem(SESSION_TOKEN_KEY); } catch {}
      const status = (refreshError as AxiosError)?.response?.status;
      // Only hard-redirect on 401 (session definitively dead).
      // 429 / 5xx are transient — redirect would create a reload loop.
      // Skip when already on /login or /auth/callback (avoids reload-loop).
      if (
        status === 401 &&
        window.location.pathname !== '/auth/callback' &&
        window.location.pathname !== '/login'
      ) {
        window.location.href = '/login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  },
);

export default api;
